<template>
  <create-dialog
    width="75%"
    :title="title"
    @closeModal="$emit('closeModal', false)"
    @handleOpen="handleOpen"
    @validate="validateForm"
    ref="createDialog"
  >
    <template slot="form">
      <div class="row">
        <div class="col-12 col-md-6">
          <div class="form-group has-label">
            <label>{{ $t('common_words.company') }}: *</label>
            <el-select
              v-model="model.business_id"
              clearable
              filterable
              class="select-primary custom-width-select shadow-sm required-business"
              size="large"
              :placeholder="`${$t('common_words.select')}...`"
              @change="handleBusinessChange"
            >
              <el-option
                v-for="business in businesses"
                :key="business.id"
                :value="business.id"
                :label="business.name"
                class="select-primary"
              />
            </el-select>
          </div>
        </div>

        <div class="col-12 col-md-6">
          <div class="form-group has-label">
            <label>{{ $t('common_words.farm') }}: *</label>
            <el-select
              v-model="model.farm_id"
              clearable
              filterable
              class="select-primary custom-width-select shadow-sm required-farm"
              size="large"
              :placeholder="`${$t('common_words.select')}...`"
              @change="syncLocation"
            >
              <el-option
                v-for="farm in filteredFarms"
                :key="farm.id"
                :value="farm.id"
                :label="farm.name"
                class="select-primary"
              />
            </el-select>
          </div>
        </div>

        <div class="col-12 col-md-6 col-lg-4">
          <fg-input
            v-model.trim="model.name"
            type="text"
            :label="`${$t('common_words.name')}: *`"
            name="name"
            v-validate="modelValidations.name"
            :error="getError('name')"
          />
        </div>

        <div class="col-12 col-md-6 col-lg-4">
          <fg-input
            v-model="model.brand"
            type="text"
            :label="$t('devices.brand')"
            name="brand"
          />
        </div>

        <div class="col-12 col-md-6 col-lg-4">
          <fg-input
            v-model="model.model"
            type="text"
            :label="$t('devices.model')"
            name="model"
          />
        </div>

        <div class="col-12 col-md-6 col-lg-4">
          <fg-input
            v-model="model.serial_number"
            type="text"
            :label="`${$t('devices.serial_number')}: *`"
            name="serial_number"
            v-validate="modelValidations.serial_number"
            :error="getError('serial_number')"
          />
        </div>

        <div class="col-12 col-md-6 col-lg-4">
          <fg-input
            v-model.trim="model.indicator_mac"
            type="text"
            :label="`${$t('devices.indicator_mac')}: *`"
            name="indicator_mac"
            v-validate="modelValidations.indicator_mac"
            :error="getError('indicator_mac')"
            placeholder="00:1A:2B:3C:4D:5E"
          />
        </div>

        <div class="col-12 col-md-6 col-lg-4">
          <fg-input
            :value="selectedFarmName"
            type="text"
            :label="`${$t('common_words.location')}: *`"
            disabled
          />
        </div>
      </div>
    </template>
  </create-dialog>
</template>

<script>
import { Axios } from 'src/class/Axios.js';
import { autoSelectElements, isset } from 'src/util/Util';
import MixinDialog from 'src/mixins/mixin-dialog';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinPermissions from 'src/util/mixinPermissions';
import MixinValidations from 'src/mixins/forms/mixin-validations';

export default {
  name: 'edit-or-create-tablet',
  props: {
    syncDialog: {
      type: Object,
      default: () => ({ create: false, disabledField: false }),
    },
  },
  mixins: [MixinDialog, MixinForm, MixinPermissions, MixinValidations],
  data() {
    const initialModel = {
      business_id: null,
      farm_id: null,
      name: null,
      brand: null,
      model: null,
      serial_number: null,
      indicator_mac: null,
      location: null,
    };

    return {
      businesses: [],
      farms: [],
      initialModel,
      model: { ...initialModel },
      modelValidations: {
        name: { required: true },
        serial_number: { required: true },
        indicator_mac: { required: true },
      },
    };
  },
  computed: {
    filteredFarms() {
      if (!isset(this.model.business_id)) return [];
      return this.farms.filter((farm) => {
        if (Number(farm.business_id) === Number(this.model.business_id)) {
          return true;
        }
        const relations = farm.businesses || farm.business || [];
        const businesses = Array.isArray(relations) ? relations : [relations];
        return businesses.some(
          (business) =>
            business && Number(business.id) === Number(this.model.business_id)
        );
      });
    },
    selectedFarmName() {
      const farm = this.farms.find(
        (item) => Number(item.id) === Number(this.model.farm_id)
      );
      return farm ? farm.name : '';
    },
  },
  watch: {
    syncDialog(value) {
      if (value.create) this.openModal('create', value.disabledField);
      else this.closeModal('create', value.disabledField);
    },
  },
  methods: {
    handleOpen() {
      if (!this.model.isEdit) {
        this.model.business_id = autoSelectElements(0, this.businesses);
        this.handleBusinessChange();
      }
    },
    handleBusinessChange() {
      this.model.farm_id = autoSelectElements(0, this.filteredFarms);
      this.syncLocation();
    },
    syncLocation() {
      this.model.location = this.selectedFarmName || null;
    },
    validateForm() {
      return this.$validator.validateAll().then((isValid) => {
        if (!isValid) return false;
        if (!isset(this.model.business_id)) {
          return this.validationInputsRequired('business');
        }
        if (!isset(this.model.farm_id)) {
          return this.validationInputsRequired('farm');
        }

        this.syncLocation();
        this.$emit('addEdit', this.model);
        return true;
      });
    },
    setEditForm(editForm) {
      this.model = {
        ...this.initialModel,
        ...editForm,
        business_id: editForm.business_id || (editForm.business || {}).id,
        farm_id: editForm.farm_id || (editForm.farm || {}).id,
        isEdit: true,
      };
      this.syncLocation();
      this.errors.clear();
    },
  },
  created() {
    if (!this.isIndex()) {
      this.showMessageAccess();
      return;
    }

    Promise.all([
      Axios.getMasters('businesses/only'),
      Axios.getMasters('farms/farms/only'),
    ])
      .then(([businesses, farms]) => {
        this.businesses = businesses.data.data || [];
        this.farms = farms.data.data || [];
      })
      .catch((error) => this.showMessageErrors(error));
  },
  updated() {
    if (sessionStorage.getItem('editForm')) {
      this.setEditForm(JSON.parse(sessionStorage.getItem('editForm')));
      sessionStorage.removeItem('editForm');
    }
  },
  beforeDestroy() {
    sessionStorage.removeItem('editForm');
  },
};
</script>
